import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { CuestionariosService } from '../services/cuestionarios.service';

@Component({
  selector: 'app-nuevo-guion-dialog',
  templateUrl: './nuevo-guion-dialog.component.html',
  providers: [MessageService]
})
export class NuevoGuionDialogComponent implements OnInit, OnChanges {
  @Input() displayDialog: boolean = false;
  @Input() cuestionario: any = null;
  @Input() preguntaSeleccionada: any = null;
  @Input() guionSeleccionado: any = null;
  @Output() displayDialogChange = new EventEmitter<boolean>();
  @Output() onHide = new EventEmitter<{refresh: boolean}>();

  form: FormGroup;
  loading: boolean = false;
  esEdicion: boolean = false;

  // Datos para los dropdowns
  tablas: { label: string, value: string }[] = [];
  tipos: { label: string, value: string }[] = [];
  campos: { label: string, value: string }[] = [];

  // Mapeo de tablas a campos
  private tablaCampoMap = {
    'cmtbl_empleos': 'tipo_ingreso',
    'cmtbl_domicilios': 'tpo_domicilio',
    'cmtbl_refpers': 'tpo_ref',
    'cmtbl_refcome': 'tpo_referencia',
    'cmtbl_propiedades': 'tipo_propiedad',
    'tmprelacion': 'tipo'
  };

  constructor(
    private fb: FormBuilder,
    private messageService: MessageService,
    private cuestionariosService: CuestionariosService
  ) {
    this.form = this.fb.group({
      sClaveCues: [{ value: '', disabled: true }, Validators.required],
      iNumPreg: [{ value: '', disabled: true }, Validators.required],
      iNumGuia: ['', Validators.required],
      sTexto: ['', Validators.required],
      sTabla: ['', Validators.required],
      sTipo: ['', Validators.required],
      sCampo: ['', Validators.required]
    });

    // Suscribirse a cambios en sTabla
    this.form.get('sTabla')?.valueChanges.subscribe(tabla => {
      if (tabla) {
        this.cargarCampos(tabla);
        this.cargarTipos(tabla);
      } else {
        this.campos = [];
        this.tipos = [];
        this.form.get('sCampo')?.setValue('');
        this.form.get('sTipo')?.setValue('');
      }
    });
  }

  ngOnInit(): void {
    this.cargarTablas();
  }

  ngOnChanges(changes: SimpleChanges): void {
    console.log('Cambios detectados:', changes);
    if (changes['displayDialog'] && this.displayDialog) {
      this.cargarDatosIniciales();
    }
  }

  cargarDatosIniciales(): void {
    console.log('Cargando datos iniciales:', {
      cuestionario: this.cuestionario,
      preguntaSeleccionada: this.preguntaSeleccionada,
      guionSeleccionado: this.guionSeleccionado
    });

    if (this.cuestionario && this.preguntaSeleccionada) {
      this.form.patchValue({
        sClaveCues: this.cuestionario.sClaveCues,
        iNumPreg: this.preguntaSeleccionada.iNumPreg
      });

      if (this.guionSeleccionado) {
        console.log('Modo edición activado');
        this.esEdicion = true;
        this.cargarDatosGuion();
      }
    }
  }

  cargarDatosGuion(): void {
    console.log('Cargando datos del guión:', this.guionSeleccionado);
    this.loading = true;
    const request = {
      que: 5,
      tope: "",
      sClaveCues: this.cuestionario.sClaveCues,
      iNumPreg: this.preguntaSeleccionada.iNumPreg,
      iNumGuia: this.guionSeleccionado.iNumGuia,
      sTexto: "",
      sTabla: "",
      sTipo: "",
      sCampo: ""
    };

    console.log('Request para cargar guión:', request);

    this.cuestionariosService.getGuiones(request).subscribe({
      next: (response) => {
        console.log('Respuesta del servicio:', response);
        if (response.success && response.data.length > 0) {
          const guion = response.data[0];
          this.form.patchValue({
            iNumGuia: guion.iNumGuia,
            sTexto: guion.sTexto,
            sTabla: guion.sTabla,
            sTipo: guion.sTipo,
            sCampo: guion.sCampo
          });

          // Cargar los dropdowns
          this.cargarCampos(guion.sTabla);
          this.cargarTipos(guion.sTabla);
        }
      },
      error: (error) => {
        console.error('Error al cargar datos del guión:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'No se pudieron cargar los datos del guión'
        });
      },
      complete: () => {
        this.loading = false;
      }
    });
  }

  cargarTablas(): void {
    this.loading = true;
    this.cuestionariosService.getTablas().subscribe({
      next: (response) => {
        if (response.success) {
          this.tablas = response.data.map(item => ({
            label: `${item.sTabla}`,
            value: item.sTabla
          }));
        }
      },
      error: (error) => {
        console.error('Error al cargar tablas:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'No se pudieron cargar las tablas'
        });
      },
      complete: () => {
        this.loading = false;
      }
    });
  }

  cargarCampos(sTabla: string): void {
    this.loading = true;
    this.cuestionariosService.getTipos(sTabla).subscribe({
      next: (response) => {
        if (response.success) {
          this.campos = response.data.map(item => ({
            label: item.sEtiqueta,
            value: item.sCampo
          }));
        }
      },
      error: (error) => {
        console.error('Error al cargar campos:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'No se pudieron cargar los campos'
        });
      },
      complete: () => {
        this.loading = false;
      }
    });
  }

  cargarTipos(sTabla: string): void {
    this.loading = true;
    const campo = this.tablaCampoMap[sTabla.toLowerCase()];

    if (campo) {
      // Si la tabla está en el mapeo, usar GetOpcionesVariables
      this.cuestionariosService.getTiposPorTabla(sTabla, campo).subscribe({
        next: (response) => {
          if (response.success) {
            this.tipos = response.data.map(item => ({
              label: item.sOpcion,
              value: item.sValor
            }));
          }
        },
        error: (error) => {
          console.error('Error al cargar tipos:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'No se pudieron cargar los tipos'
          });
        },
        complete: () => {
          this.loading = false;
        }
      });
    } else {
      // Si no está en el mapeo, usar Customizables
      this.cuestionariosService.getCustomizables().subscribe({
        next: (response) => {
          if (response.success) {
            this.tipos = response.data.map(item => ({
              label: item.descripcion,
              value: item.caracteristica
            }));
          }
        },
        error: (error) => {
          console.error('Error al cargar tipos:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'No se pudieron cargar los tipos'
          });
        },
        complete: () => {
          this.loading = false;
        }
      });
    }
  }

  onSubmit(): void {
    if (this.form.valid) {
      this.loading = true;
      const formValue = this.form.getRawValue();
      
      const request = {
        que: this.esEdicion ? 2 : 1, // 1 para crear, 2 para actualizar
        sClaveCues: this.cuestionario.sClaveCues,
        iNumPreg: this.preguntaSeleccionada.iNumPreg,
        iNumGuia: formValue.iNumGuia,
        sTexto: formValue.sTexto,
        sTabla: formValue.sTabla,
        sTipo: formValue.sTipo,
        sCampo: formValue.sCampo
      };

      this.cuestionariosService.guardarGuion(request).subscribe({
        next: (response) => {
          if (response.success && response.data.error === "0") {
            this.messageService.add({
              severity: 'success',
              summary: 'Éxito',
              detail: this.esEdicion ? 'Guión actualizado correctamente' : 'Guión guardado correctamente'
            });
            this.onDialogClosed();
            this.onHide.emit({ refresh: true });
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: this.esEdicion ? 'No se pudo actualizar el guión' : 'No se pudo guardar el guión'
            });
          }
        },
        error: (error) => {
          console.error('Error:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: this.esEdicion ? 'No se pudo actualizar el guión' : 'No se pudo guardar el guión'
          });
        },
        complete: () => {
          this.loading = false;
        }
      });
    } else {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Por favor complete todos los campos requeridos'
      });
    }
  }

  limpiarFormulario(): void {
    this.form.reset({
      sClaveCues: this.cuestionario?.sClaveCues || '',
      iNumPreg: this.preguntaSeleccionada?.iNumPreg || ''
    });
    this.campos = [];
    this.tipos = [];
    this.esEdicion = false;
    this.guionSeleccionado = null;
  }

  onDialogClosed(): void {
    this.displayDialog = false;
    this.displayDialogChange.emit(this.displayDialog);
    this.onHide.emit({ refresh: false });
    this.limpiarFormulario();
  }
} 